import React, {Dispatch, FC, SetStateAction, useContext, useEffect, useState} from 'react';
import {useStoreon} from 'storeon/react';
import {useFormik} from 'formik';
import cnBind from 'classnames/bind';
import dayjs from 'dayjs';
import {GxButton, GxCheckbox, GxForm, GxInput} from '@garpix/garpix-web-components-react';
import { api } from '@/api';
import { IRetrieveOrder } from '@/types';
import styles from './styles/index.module.scss';
import {NAME_STATUS, TYPES_STATUS} from '../../../../consts';
import Radio from '../../../../../../components/Radio';
import {Context, PAYMENT_RETURN_PAY_ITEMS, RETURN_PAY_TYPES, TYPE_MODAL_ORDER} from '../../const';
import InputNumbers from '../../../../../../components/InputNumbers';
import {ORDERS} from '../../../../../../const';
import {contains, countReturnResurcePayment, getReturnableOrders, infoPriceReturnPay, returnPaymentOrders} from '../../helpers';
import useModalFlag from '../../../../../../hooks/useModalFlag';
import ModalOrder from '../../index';
import { IOrderInfo } from '../../interfaces';
import { IFormikReturnPay } from './interfaces';

interface IReturnPay {
    onBack: () => void;
    setOrderInfo: Dispatch<SetStateAction<IOrderInfo>>
    data: IRetrieveOrder,
}

const ReturnPay: FC<IReturnPay> = ({onBack}) => {
    const cn = cnBind.bind(styles);
    const [{info}, setOrderInfo] = useContext<any>(Context);
    const {id, pay_order, is_pay_here} = info;
    const returnableOrders = getReturnableOrders(pay_order.orders);
    const side = (returnableOrders[0] || pay_order.orders[0])?.side;
    const {certificate_rent_pay_count = 0} = pay_order;
    const isCertificatePaid = pay_order.orders.filter((order: any) => (order.certificate_name)).length > 0;
    const isResDepositPaid = pay_order.orders.filter((order: any) => (order.deposit_name)).length > 0;
    const {dispatch} = useStoreon();
    const {price, price_paid} = infoPriceReturnPay(pay_order);
    const [ordersId, setOrdersId] = useState<number[]>([]);
    const {flag: modalOrderOpen, setModalFlag: toggleFlagModalOrder} = useModalFlag();
    const [orderModalInfo, setOrderModalInfo] = useState<Record<string, any> | null>({});
    const [loading, setLoading] = useState(true)

    const formik = useFormik<IFormikReturnPay>({
        enableReinitialize: true,
        initialValues: {amount:0, comment: '', return_method: 0, is_delete: false, date: null, isAll: false, resources_total: 0},
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values) => {
            await returnPaymentOrders({ ordersId, values });
            const order = await api.order.getOrder(id);
            setOrderInfo({
                visibleItem: TYPE_MODAL_ORDER.RETURN_PAY,
                info: order
            });
            setOrdersId([])
            getInfoPayment();

            dispatch('orders/get');
        }
    });

    const changeItem = (id: number, flag: boolean) => {
        if (flag) {
            const arr: any = ordersId.concat([id]);
            setOrdersId(arr);
        } else {
            setOrdersId(ordersId.filter((item: number) => id !== item));
        }
    };
    const changeAllItem = (orders: Array<any>, flag: boolean) => {
        if (flag) {
            let data: any = [];
            orders.forEach((item: Record<string, any>) => {
                data.push(item.id);
            });
            setOrdersId(data);
        } else {
            setOrdersId([]);
        }
        formik.setFieldValue('isAll', flag);
    };

    const getInfoPayment = async () => {
        setLoading(true)
        const data = await api.payment.lastPayment({
            pay_order_id: pay_order.id,
            is_last: true
        });
        setLoading(false)
        formik.setFieldValue('date', data.created_at);
        if (RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES === data.payment_method || RETURN_PAY_TYPES.CERTIFICATE === data.payment_method) {
            formik.setFieldValue('return_method', data.payment_method);
        } else {
            formik.setFieldValue('return_method', RETURN_PAY_TYPES.MONEY_DEPOSITS);
        }
    };
    const handleClickModal = (date: string | null, order: Record<string, any> | null) => {
        setOrderModalInfo({date, order});
        toggleFlagModalOrder();
    };
    useEffect(() => {
        getInfoPayment();
        if(returnableOrders.length === 1) setOrdersId([returnableOrders[0].id]);
    }, []);
    useEffect(() => {
        if (returnableOrders.length > 0 && ordersId.length === returnableOrders.length) {
            formik.setFieldValue('isAll', true);
        } else {
            formik.setFieldValue('isAll', false);
        }
    }, [ordersId]);

    useEffect(() => {
        const {amount, total_paid} = countReturnResurcePayment(formik.values.return_method, returnableOrders, ordersId)
        formik.setFieldValue('amount', amount)
        formik.setFieldValue('resources_total', total_paid)
    }, [formik.values.return_method, ordersId]);
    const isReturnWithResources =
        formik.values.return_method === RETURN_PAY_TYPES.CERTIFICATE ||
        formik.values.return_method === RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES;

    const isRentResource = side === ORDERS.WAVE && certificate_rent_pay_count > 0;
    const resourcesUnitInput = side === ORDERS.PIPE ? 'мин.' : (isRentResource ? 'ар.' : 'сет(ов)');
    const resourcesUnitText = side === ORDERS.PIPE ? 'мин.' : (isRentResource ? 'ар.' : 'сет.');

    useEffect(() =>{
        if (isCertificatePaid){
            formik.setFieldValue('return_method', RETURN_PAY_TYPES.CERTIFICATE)
        }
    }, [isCertificatePaid])

    return (
        <>
        <GxForm onGx-submit={formik.submitForm}>
            <div className={styles['subheader']}>
                <span className={styles['subheader--subtitle']}>Способ возврата:</span>
            </div>
            <div className={styles['actions']}>
                {PAYMENT_RETURN_PAY_ITEMS
                    .filter((item) => (is_pay_here || isCertificatePaid || isResDepositPaid) ? item.id !== RETURN_PAY_TYPES.ONLINE : true)
                    .map((item) => {
                       return  (
                            <Radio
                                key={item.id}
                                name="return_method" className={styles['actions--item']}
                                checked={formik.values.return_method === item.id}
                                disabled={formik.values.return_method === RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES || isCertificatePaid}
                                onClick={() => formik.setFieldValue('return_method', item.id)}
                            >
                                {item.title}
                            </Radio>
                        )
                    })}
                {isCertificatePaid &&
                    <Radio
                      name="return_method" className={styles['actions--item']}
                      checked={formik.values.return_method === RETURN_PAY_TYPES.CERTIFICATE}
                      onClick={() => formik.setFieldValue('return_method', RETURN_PAY_TYPES.CERTIFICATE)}>
                      На сертификат
                    </Radio>
                }
                {isResDepositPaid &&
                    <Radio
                      name="return_method" className={styles['actions--item']}
                      checked={formik.values.return_method === RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES}
                      onClick={() => formik.setFieldValue('return_method', RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES)}>
                      На депозит
                    </Radio>
                }
            </div>
            <div className={styles['wrap-group']}>
                {formik.values.return_method === RETURN_PAY_TYPES.CERTIFICATE || formik.values.return_method === RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES ?
                    <div className={styles['group']}>
                        <InputNumbers
                            label='Сумма возврата:'
                            name="amount"
                            value={formik.values.amount}
                            disabled
                            className={cn({
                                'group--input': true,
                                'group--input--error': formik.errors.amount
                            })}
                            type='number'
                        />
                        <span className={styles['symbol--resurs']}>
                            {resourcesUnitInput}
                        </span>
                        <p className={styles['group--error']}>{formik.errors.amount}</p>
                    </div>
                    :
                    <div className={styles['group']}>
                    <InputNumbers
                        label='Сумма возврата:'
                        name="amount"
                        value={formik.values.amount}
                        disabled
                        className={cn({
                            'group--input': true,
                            'group--input--error': formik.errors.amount
                        })}
                        type='number'
                    />
                    <span className={styles['symbol--price']}/>
                    <p className={styles['group--error']}>{formik.errors.amount}</p>
                </div>
                }
                <div className={styles['group']}>
                    <GxInput
                        label='Примечание:'
                        name="comment"
                        onGx-change={(e: Record<string, any>) => {
                            formik.setFieldValue('comment', e.target.value);
                        }}
                        className={cn(styles['group--input'])}
                    />
                </div>
            </div>
            <div className={cn(styles['subheader'], styles['subheader--orders'])}>
                <span className={styles['subheader--subtitle']}>Заявки</span>
                {/* {((!pay_order.is_mass_booking && is_pay_here) || (formik.values.return_method !== RETURN_PAY_TYPES.CERTIFICATE && formik.values.return_method !== RETURN_PAY_TYPES.FROM_DEPOSIT_MINUTES && formik.values.return_method !== RETURN_PAY_TYPES.MONEY_DEPOSITS) ) */}
                {/*    ? <GxCheckbox name='is_delete' onGx-change={(e:any)=>formik.setFieldValue('is_delete', e.target.checked)} className={styles['checkbox']}>Удалить заявку после */}
                {/*        возврата</GxCheckbox> */}
                {/*    : */}
                    <span className={styles['subheader--subtitle']}>Все заявки будут удалены</span>
            </div>
            {returnableOrders.length > 1  &&
            <div className={styles['footer--left-block']}>
              <GxCheckbox
                className={styles['footer--pay']}
                name='is_all'
                checked={formik.values.isAll}
                onGx-change={(e: Record<string, any>) => changeAllItem(returnableOrders, e.target.checked)}>Все заявки</GxCheckbox>
            </div>
            }
            <div className={cn(styles['table'], styles['table--orders'])}>
                <table className={styles['orders']}>
                    <thead>
                    <tr>
                        <th>№ Заявки</th>
                        <th>Тариф</th>
                        <th>Дата</th>
                        <th>Время</th>
                        <th>Цена</th>
                        <th>Длительность</th>
                        <th>Статус</th>
                    </tr>
                    </thead>
                    <tbody>
                    {typeof pay_order !== 'number' && returnableOrders.map((item: Record<string, any>) => (
                        <tr key={item.id}>
                            <td>
                                {(returnableOrders.length > 1)
                                && <GxCheckbox
                                  onGx-change={(e: Record<string, any>) => changeItem(item.id, e.target.checked)}
                                  checked={contains(ordersId, item.id)}
                                  className={styles['footer--pay']}/>}
                                <span
                                    onClick={() => handleClickModal(item.start_at, item)}
                                    className={styles['name']}
                                    role='button'
                                    tabIndex={0}
                                > № {item.number}</span>
                            </td>
                            <td>{item.tariff.title}</td>
                            <td>{dayjs(item.start_at).format('DD.MM.YYYY')}</td>
                            <td>{dayjs(item.start_at).format('HH:mm')}</td>
                            <td className={styles['table--item_price']}>{Number(item.price)}</td>
                            <td>
                                {item.side === ORDERS.PIPE ? `${item.tariff_rate.minutes} мин.` : `${item.tariff_rate.count_people} чел.`}
                            </td>
                            <td>
                      <span
                          className={cn(styles['table--item__status'], styles[`table--item__status--${NAME_STATUS[TYPES_STATUS[item.status]].class}`])}>
                        {NAME_STATUS[TYPES_STATUS[item.status]].title}
                      </span>
                            </td>
                        </tr>
                    ))}
                    {typeof pay_order !== 'number' && returnableOrders.length === 0 && (
                        <tr>
                            <td colSpan={7}>Нет заявок для возврата</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
            <div className={styles['footer']}>
                <div className={styles['footer--left-block']}>
                    <div className={styles['pay-info']}>
                        <div className={styles['pay-info__item']}>
                            <p className={styles['pay-info__item_label']}>Сумма заказа:</p>
                            {isReturnWithResources ?
                                <p>
                                    {formik.values.resources_total.toLocaleString()} {resourcesUnitText}
                                </p> :
                                <p className={styles['pay-info__item_price']}>{price.toLocaleString()} ₽</p>
                            }
                        </div>
                        <div className={cn(styles['pay-info__item'], styles['pay-info__item_paid'])}>
                            <p className={styles['pay-info__item_label']}>Оплачено:</p>
                            {isReturnWithResources ?
                                <p>
                                    {formik.values.amount.toLocaleString()} {resourcesUnitText}
                                </p> :
                                <p className={styles['pay-info__item_price']}>{price_paid.toLocaleString()} ₽</p>
                            }
                        </div>
                        <div className={cn(styles['pay-info__item_container'])}>
                            <div className={cn(styles['pay-info__item'])}>
                                <p className={styles['pay-info__item_label']}>
                                    К возврату: </p>
                                {isReturnWithResources ?
                                    <p>
                                        {formik.values.amount.toLocaleString()} {resourcesUnitText}
                                    </p> :
                                    <p className={styles['pay-info__item_price']}>{formik.values.amount.toLocaleString()} ₽</p>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles['time']}>
                    <p className={styles['time--info']}>Дата последнего платежа:</p>
                    <p className={styles['time--date']}>{formik.values.date && dayjs(formik.values.date).format('DD.MM.YYYY HH:mm')}</p>
                </div>
                <div className={styles['footer--right-block']}>
                    <GxButton className={styles['btn--gray']} onClick={onBack}>
                        Отмена
                    </GxButton>
                    <GxButton className={styles['btn--default']} type='submit' disabled={loading || ordersId.length === 0}>
                        Вернуть
                    </GxButton>
                </div>
            </div>
        </GxForm>
            {modalOrderOpen &&
            <ModalOrder data={orderModalInfo?.order} date={orderModalInfo?.date} flag={modalOrderOpen} closeModal={toggleFlagModalOrder}/>}
        </>
    );
};

export default ReturnPay;
